/*
 * Run detail (U24). The operator's single-run surface:
 *
 *   - the state-machine progress rail (current stage + the path through the 17
 *     states), kept live by folding the latest WS frame onto the run query;
 *   - the live per-stage/per-ticket status feed streaming over the run's WS
 *     (`useRunStatus`) — connection state + the latest frames;
 *   - the control bar (pause / resume / cancel; cancel confirms — irreversible),
 *     gated to operator via `<RequireRole capability="operate">`;
 *   - the needs_human / recovery items, rendered actionable inline (verdict +
 *     recommended action + a link into the full report);
 *   - a "Failed tickets" panel (when the run has ok:false preview rows) so
 *     the operator can see what failed and why without digging into logs.
 *
 * The run query is the authoritative record for `control` (the WS frame carries
 * only stage+status); we refetch it on every WS frame so a control transition the
 * worker drives lands in the UI without a manual reload.
 */

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, Badge, Box, Button, Group, Image, Stack, Table, Text, Title } from "@mantine/core";
import type { PreviewTicketEntry } from "../api/client";
import { StatusPill } from "../components/StatusPill";
import { TeachAliasModal } from "../components/aliases/TeachAliasModal";
import { RequireRole } from "../auth/RequireAuth";
import {
  useRun,
  useRunInconsistencies,
  useRunPreview,
  queryKeys,
} from "../api/queries";
import { errorMessage } from "../api/errors";
import { useRunStatus } from "../ws/useRunStatus";
import {
  PipelineProgress,
  LiveStatusStream,
  ControlBar,
  RecoveryItems,
} from "../components/pipeline";
import { formatDateTime } from "../lib/format";
import type { ReactNode } from "react";
import styles from "../components/pipeline/pipeline.module.css";

/** The page title block (Mantine `Group`/`Title`/`Text`). */
function PageHeader({
  title,
  description,
  actions,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <Group justify="space-between" align="flex-start" wrap="nowrap" mb="lg">
      <Stack gap={4}>
        <Title order={1} fz="h2">
          {title}
        </Title>
        {description && (
          <Text c="dimmed" fz="sm">
            {description}
          </Text>
        )}
      </Stack>
      {actions && <div>{actions}</div>}
    </Group>
  );
}

/** A titled empty/loading state (Mantine `Stack`/`Text`). */
function EmptyState({
  title,
  description,
  icon,
}: {
  title: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <Stack align="center" gap="sm" py="xl" px="md" ta="center">
      {icon && (
        <Text aria-hidden="true" fz={28} lh={1}>
          {icon}
        </Text>
      )}
      <Text fw={600} fz="lg">
        {title}
      </Text>
      {description && (
        <Text c="dimmed" fz="sm" maw={420}>
          {description}
        </Text>
      )}
    </Stack>
  );
}

/** Monospace inline cell helper for ids/timestamps. */
function Mono({ children }: { children: ReactNode }) {
  return (
    <Text component="span" ff="monospace" fz="sm">
      {children}
    </Text>
  );
}

/** Terminal + stalled states where we want to surface prep failures. */
const FAILED_STATES = new Set([
  "failed",
  "aborted",
  "needs_human",
  "audited_with_discrepancies",
]);

/** One flattened ad row for the compact preview table (mirrors the launch
 *  Step-4 Preview's buildAdRows). A failed ticket becomes a single error row. */
interface DetailAdRow {
  id: string;
  ticketFailed: boolean;
  concept: string;
  adName: string | null;
  adsetLabel: string;
  headline: string | null;
  cta: string;
  copy: string;
  mediaUrl: string | null;
  error: string | null;
}

function buildDetailRows(tickets: PreviewTicketEntry[]): DetailAdRow[] {
  const rows: DetailAdRow[] = [];
  for (const t of tickets) {
    if (!t.ok) {
      rows.push({
        id: `${t.ticket_id}-failed`,
        ticketFailed: true,
        concept: t.concept,
        adName: null,
        adsetLabel: t.adset_name ?? "—",
        headline: t.headline,
        cta: "—",
        copy: "—",
        mediaUrl: null,
        error: t.error ?? "Unknown error",
      });
      continue;
    }
    for (const ad of t.ads) {
      rows.push({
        id: ad.id,
        ticketFailed: false,
        concept: t.concept,
        adName: ad.ad_name,
        adsetLabel: ad.ad_sets.map((a) => a.label).join(", ") || t.adset_name || "—",
        headline: ad.title || t.headline,
        cta: ad.cta,
        copy: ad.description,
        mediaUrl: ad.media_url,
        error: null,
      });
    }
  }
  return rows;
}

function Dash() {
  return (
    <Text component="span" c="dimmed">
      —
    </Text>
  );
}

const DETAIL_VIDEO_RE = /\.(mp4|mov|webm)(?:[?#]|$)/i;

/** Small read-only creative thumbnail (image or video poster). */
function DetailThumb({ src }: { src: string | null }) {
  const box = { width: 44, height: 44, borderRadius: "var(--mantine-radius-sm)" } as const;
  if (!src || !/^https?:\/\//i.test(src)) {
    return (
      <Box
        aria-hidden
        style={{ ...box, border: "1px solid var(--mantine-color-gray-3)", background: "var(--mantine-color-gray-1)" }}
      />
    );
  }
  if (DETAIL_VIDEO_RE.test(src)) {
    return <video src={src} muted preload="metadata" style={{ ...box, objectFit: "cover", display: "block" }} />;
  }
  return <Image src={src} w={44} h={44} radius="sm" fit="cover" alt="" />;
}

/**
 * U9 — the run's tickets + selected ads rendered as a compact preview table that
 * mirrors the launch Step-4 Preview (thumbnail · Ad · Ad set · Headline · CTA ·
 * truncated Copy), so an admin can troubleshoot a historic run without a wall of
 * text. The "selected" set is reconstructed from config.selected_ad_ids; null/empty
 * means "all ready ads" (no explicit selection was recorded).
 */
function RunTicketsPanel({
  tickets,
  selectedAdIds,
}: {
  tickets: PreviewTicketEntry[];
  selectedAdIds: string[] | null;
}) {
  const selectedSet =
    selectedAdIds && selectedAdIds.length > 0 ? new Set(selectedAdIds) : null;
  const rows = buildDetailRows(tickets);

  return (
    <section className={styles.panel} aria-label="Tickets">
      <div className={styles.panelHeader}>
        <h2 className={styles.panelTitle}>Tickets ({tickets.length})</h2>
      </div>
      {tickets.length === 0 ? (
        <Text c="dimmed" fz="sm">
          No tickets were prepped for this run.
        </Text>
      ) : (
        <Stack gap="sm">
          {selectedSet === null && (
            <Text fz="xs" c="dimmed">
              Selection: all ready ads (no explicit selection recorded).
            </Text>
          )}
          <Table.ScrollContainer minWidth={760}>
            <Table highlightOnHover verticalSpacing="xs" striped>
              <Table.Thead>
                <Table.Tr>
                  {selectedSet !== null && <Table.Th w={80}>Selected</Table.Th>}
                  <Table.Th w={56} aria-label="Creative" />
                  <Table.Th>Ad</Table.Th>
                  <Table.Th>Ad set</Table.Th>
                  <Table.Th>Headline</Table.Th>
                  <Table.Th w={130}>CTA</Table.Th>
                  <Table.Th>Copy</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((row) => {
                  const isSelected = selectedSet === null || selectedSet.has(row.id);
                  const dim = selectedSet !== null && !row.ticketFailed && !isSelected;
                  return (
                    <Table.Tr key={row.id} style={dim ? { opacity: 0.5 } : undefined}>
                      {selectedSet !== null && (
                        <Table.Td>
                          {!row.ticketFailed && isSelected ? (
                            <Badge size="xs" color="blue" variant="light" aria-label="Selected">
                              ✓
                            </Badge>
                          ) : (
                            <Dash />
                          )}
                        </Table.Td>
                      )}
                      <Table.Td>
                        <DetailThumb src={row.mediaUrl} />
                      </Table.Td>
                      <Table.Td>
                        <Text
                          fz="sm"
                          td={row.ticketFailed ? "line-through" : undefined}
                          c={row.ticketFailed ? "dimmed" : undefined}
                        >
                          {row.concept}
                        </Text>
                        {row.adName && (
                          <Text fz="xs" c="dimmed">
                            {row.adName}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>{row.adsetLabel === "—" ? <Dash /> : row.adsetLabel}</Table.Td>
                      <Table.Td>
                        {row.ticketFailed || !row.headline ? <Dash /> : row.headline}
                      </Table.Td>
                      <Table.Td>
                        {row.ticketFailed ? (
                          <Text fz="xs" c="red" role="note" aria-label="Error">
                            ✗ {row.error}
                          </Text>
                        ) : (
                          row.cta || <Dash />
                        )}
                      </Table.Td>
                      <Table.Td>
                        {row.ticketFailed ? null : row.copy ? (
                          <Text
                            fz="xs"
                            c="dimmed"
                            lineClamp={2}
                            title={row.copy}
                            style={{ whiteSpace: "pre-wrap" }}
                          >
                            {row.copy}
                          </Text>
                        ) : (
                          <Dash />
                        )}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Stack>
      )}
    </section>
  );
}

export function RunDetailPage() {
  const { id } = useParams<{ id: string }>();
  const runId = id ?? "";
  const qc = useQueryClient();

  const runQuery = useRun(runId, { enabled: Boolean(runId) });
  const { latest, history, connection } = useRunStatus(runId, {
    enabled: Boolean(runId),
  });

  // Each live frame may reflect a control transition the worker drove (e.g. a
  // drain completing → PAUSED); refetch the authoritative run record so the
  // control bar + summary stay in sync without a manual reload.
  useEffect(() => {
    if (!runId || !latest) return;
    void qc.invalidateQueries({ queryKey: queryKeys.run(runId) });
  }, [latest, runId, qc]);

  const run = runQuery.data;

  // The report is only meaningful once a run has reached/passed audit; fetch it
  // and surface its rows inline, but tolerate a 404 (no report yet) quietly.
  const reportQuery = useRunInconsistencies(runId, {
    enabled: Boolean(runId) && Boolean(run),
    retry: false,
  });

  // Fetch the prep preview for any terminal/historic run (or a failed one) so the
  // operator can see its tickets + selected ads. `run.status` arrives UPPERCASE from
  // the API (e.g. 'DRAFTED'), while FAILED_STATES + the literal below are lowercase —
  // so we MUST case-normalize before comparing (the old code compared an uppercase
  // status against lowercase sets and never matched). A 404 (nothing prepped) is
  // tolerated quietly via `retry: false`.
  const normStatus = (run?.status ?? "").toLowerCase();
  const wantsPreview =
    Boolean(run) &&
    (FAILED_STATES.has(normStatus) || normStatus === "completed" || normStatus === "drafted");
  const previewQuery = useRunPreview(runId, wantsPreview, { retry: false });

  // Teach-alias modal target (batch 5 #7) — a failed ticket's concept; the
  // natural loop is teach → Re-drive (prep re-runs and matches the taught row).
  const [teachConcept, setTeachConcept] = useState<string | null>(null);
  const [taughtConcept, setTaughtConcept] = useState<string | null>(null);

  if (!runId) {
    return (
      <EmptyState icon="◷" title="No run selected" />
    );
  }

  if (runQuery.isLoading) {
    return (
      <>
        <PageHeader title="Run detail" />
        <EmptyState icon="◷" title="Loading run…" />
      </>
    );
  }

  if (runQuery.isError || !run) {
    return (
      <>
        <PageHeader title="Run detail" />
        <Alert color="red" variant="light" role="alert">
          {runQuery.error
            ? errorMessage(runQuery.error)
            : "This run could not be found."}
        </Alert>
      </>
    );
  }

  // Live status from the freshest WS frame, falling back to the run record.
  const liveStatus = latest?.status ?? run.status;
  const liveStage = latest?.stage ?? run.current_stage;

  const report = reportQuery.data;

  // Tickets that failed prep (ok:false) surface here so the operator sees the
  // reason without opening the preview page.
  const failedTickets = (previewQuery.data?.tickets ?? []).filter((t) => !t.ok);

  // 2026-11-02: new ad sets that could not be set to "Acquire new customers"
  // even after a retry. They launched anyway and can spend on existing buyers.
  const acquireMisses = (previewQuery.data?.tickets ?? []).flatMap((t) =>
    (t.acquire_new_customers ?? [])
      .filter((a) => !a.ok)
      .map((a) => ({ concept: t.concept, adsetId: a.adset_id, error: a.error })),
  );

  return (
    // data-fullbleed: opt out of the Shell's centered max-width so the run detail
    // (incl. the wide preview table) uses the full content width, like the wizard.
    <Box data-fullbleed w="100%">
      <PageHeader
        title={<>Run <Mono>{run.id.slice(0, 8)}</Mono></>}
        description="Live stage progress, controls, and manual-review items."
        actions={
          <RequireRole capability="operate" fallback={<></>}>
            <ControlBar
              runId={run.id}
              status={run.status}
              control={run.control}
            />
          </RequireRole>
        }
      />

      <div className={styles.stack}>
        <PipelineProgress
          status={liveStatus}
          control={run.control}
          currentStage={liveStage}
        />

        {liveStatus.toLowerCase() === "drafted" && (
          <Alert color="blue" variant="light" title="Saved as draft" role="status">
            Creatives are staged in AdManage and Notion is updated — this run was{" "}
            <strong>not pushed to Meta</strong>. It rests in a terminal{" "}
            <em>Drafted</em> state; there are no Meta verdicts to audit.
          </Alert>
        )}

        {run.error && (
          <Alert color="red" variant="light" role="alert">
            <strong>
              This run failed
              {run.current_stage ? ` during ${run.current_stage}` : ""}.
            </strong>{" "}
            {run.error}
          </Alert>
        )}

        <section className={styles.panel} aria-label="Run summary">
          <div className={styles.metaGrid}>
            <div className={styles.metaCell}>
              <span className={styles.metaLabel}>Product</span>
              <span className={styles.metaValue}>{run.product_name}</span>
            </div>
            <div className={styles.metaCell}>
              <span className={styles.metaLabel}>Ads</span>
              <span className={styles.metaValue}>{run.n_ads}</span>
            </div>
            <div className={styles.metaCell}>
              <span className={styles.metaLabel}>Status</span>
              <span className={styles.metaValue}>
                <StatusPill kind="run" value={run.status} />
              </span>
            </div>
            <div className={styles.metaCell}>
              <span className={styles.metaLabel}>Audit</span>
              <span className={styles.metaValue}>
                {liveStatus.toLowerCase() === "drafted" ? (
                  <StatusPill tone="neutral" label="Not audited" hideDot />
                ) : run.clean ? (
                  <StatusPill tone="success" label="Clean" hideDot />
                ) : (
                  <StatusPill tone="warning" label="Discrepancies" hideDot />
                )}
              </span>
            </div>
            <div className={styles.metaCell}>
              <span className={styles.metaLabel}>Created</span>
              <span className={styles.metaValue}>{formatDateTime(run.created_at)}</span>
            </div>
            <div className={styles.metaCell}>
              <span className={styles.metaLabel}>Updated</span>
              <span className={styles.metaValue}>{formatDateTime(run.updated_at)}</span>
            </div>
          </div>
        </section>

        <LiveStatusStream
          history={history}
          latest={latest}
          connection={connection}
        />

        {acquireMisses.length > 0 && (
          <Alert
            color="orange"
            variant="light"
            role="alert"
            title={`${acquireMisses.length} new ad set${acquireMisses.length === 1 ? " is" : "s are"} NOT set to acquire new customers`}
          >
            They launched without the goal, so they can spend on existing buyers. Set
            &quot;Acquire new customers&quot; on each in Ads Manager, with the buyer lists as
            existing customers. The product&apos;s media buyer also gets a Slack DM when the
            product has a digest Slack member ID.
            <ul>
              {acquireMisses.map((m) => (
                <li key={m.adsetId}>
                  <Mono>{m.adsetId}</Mono> ({m.concept}): {m.error ?? "unknown error"}
                </li>
              ))}
            </ul>
          </Alert>
        )}

        {failedTickets.length > 0 && (
          <section className={styles.panel} aria-label="Failed tickets">
            <div className={styles.panelHeader}>
              <h2 className={styles.panelTitle}>
                Failed tickets ({failedTickets.length})
              </h2>
            </div>
            {taughtConcept && (
              <Alert color="blue" variant="light" role="status" mb="sm">
                Alias taught for <strong>{taughtConcept}</strong> — press{" "}
                <strong>Re-drive</strong> above to retry the run with it.
              </Alert>
            )}
            <ul className={styles.recoveryList}>
              {failedTickets.map((t) => (
                <li key={t.ticket_id} className={`${styles.recoveryItem} ${styles.recoveryItemActionable}`}>
                  <div className={styles.recoveryBody}>
                    <div className={styles.recoveryMeta}>
                      <strong className={styles.recoveryConcept}>{t.concept}</strong>
                    </div>
                    {t.error && (
                      <div className={styles.recoveryAction}>
                        <span className={styles.recoveryActionLabel}>Error:</span>
                        {t.error}
                      </div>
                    )}
                    {/* Batch 5 #7: a no-sheet-row prep failure is teachable in
                        place — map the concept, then Re-drive the run. */}
                    {/no master sheet row matches/i.test(t.error ?? "") && (
                      <RequireRole capability="operate" fallback={<></>}>
                        <Box mt={4}>
                          <Button
                            variant="default"
                            size="compact-xs"
                            onClick={() => setTeachConcept(t.concept)}
                          >
                            Teach alias…
                          </Button>
                        </Box>
                      </RequireRole>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        <TeachAliasModal
          open={teachConcept !== null}
          productKey={run.product_key}
          concept={teachConcept ?? ""}
          onClose={() => setTeachConcept(null)}
          onTaught={() => setTaughtConcept(teachConcept)}
        />

        {previewQuery.data && (
          <RunTicketsPanel
            tickets={previewQuery.data.tickets}
            selectedAdIds={previewQuery.data.selected_ad_ids ?? null}
          />
        )}

        {report && (report.inconsistencies.length > 0 || report.recheck_recommended) && (
          <RecoveryItems
            runId={run.id}
            rows={report.inconsistencies}
            recheckRecommended={report.recheck_recommended}
          />
        )}
      </div>
    </Box>
  );
}
